/**
 * Supabase Client Singleton
 *
 * Shared Supabase client used by all real service implementations.
 * Configured with auth auto-refresh and session persistence.
 *
 * Usage:
 *   import { supabase } from "./supabase-client.ts";
 *   const { data } = await supabase.from("profiles").select("*");
 */

import { createClient, type SupabaseClient } from "@supabase/supabase-js";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;

let _client: SupabaseClient | null = null;

// ── Provider token capture ──
// Supabase does NOT persist provider_token in localStorage — it's only
// available in the SIGNED_IN auth event fired when processing the OAuth
// callback URL. We capture it here so checkPendingConnect() can read it.
let _capturedProviderToken: string | null = null;
let _capturedProviderRefreshToken: string | null = null;
let _providerTokenResolve: (() => void) | null = null;

export function getSupabaseClient(): SupabaseClient {
  if (_client) return _client;

  if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
    throw new Error(
      "Missing Supabase environment variables. " +
      "Set VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY in your .env file."
    );
  }

  // Custom fetch that injects auth headers by reading the token directly
  // from localStorage. This avoids the SupabaseClient's internal
  // fetchWithAuth → _getAccessToken → getSession chain which deadlocks
  // when the GoTrueClient's _initialize hasn't completed yet.
  const customFetch: typeof fetch = async (input, init) => {
    const headers = new Headers(init?.headers);

    if (!headers.has("apikey")) {
      headers.set("apikey", SUPABASE_PUBLISHABLE_KEY);
    }

    if (!headers.has("Authorization")) {
      const token = getAccessTokenDirect();
      headers.set("Authorization", `Bearer ${token}`);
    }

    return fetch(input, { ...init, headers });
  };

  _client = createClient(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
      // Implicit flow for Supabase auth sessions.
      // Integration connects (Google Drive, Calendar) use a separate server-side
      // OAuth code exchange in integration-manage, which obtains refresh tokens
      // directly from Google. The implicit flow here is only for Supabase user auth.
      flowType: "implicit",
      // Bypass Navigator Locks API to prevent deadlocks during initialization.
      // The lock only coordinates token refresh across multiple tabs;
      // without it, worst case is a redundant refresh call which is harmless.
      // See: https://github.com/supabase/supabase-js/issues/1594
      lock: async <R>(
        _name: string,
        _acquireTimeout: number,
        fn: () => Promise<R>
      ): Promise<R> => {
        return await fn();
      },
    },
    global: {
      fetch: customFetch,
    },
  });

  // Register immediately after creation — BEFORE initialization completes —
  // so we catch the SIGNED_IN event that carries the transient provider_token.
  _client.auth.onAuthStateChange((_event, session) => {
    if (session?.provider_token) {
      _capturedProviderToken = session.provider_token;
      _capturedProviderRefreshToken = session.provider_refresh_token ?? null;

      // Notify waitForProviderToken() callers
      _providerTokenResolve?.();
      _providerTokenResolve = null;

      // Implicit flow puts auth tokens in the URL hash (#access_token=...),
      // which overwrites our hash-based route (e.g. #integrations).
      // Restore the route so the app navigates back to the integrations view.
      const pendingConnect = sessionStorage.getItem("katanoid_pending_integration");
      if (pendingConnect) {
        window.location.hash = "integrations";
      }
    }
  });

  return _client;
}

/**
 * Read and consume the captured provider_token from the most recent OAuth sign-in.
 * Returns null if no provider_token was captured. One-time use — clears after reading.
 */
export function consumeProviderToken(): {
  providerToken: string | null;
  providerRefreshToken: string | null;
} {
  const result = {
    providerToken: _capturedProviderToken,
    providerRefreshToken: _capturedProviderRefreshToken,
  };
  _capturedProviderToken = null;
  _capturedProviderRefreshToken = null;
  return result;
}

/**
 * Wait for the provider_token to be captured from the OAuth redirect.
 * Resolves immediately if already captured, otherwise waits up to `timeoutMs`.
 * Returns the captured tokens (and clears them) or nulls if timed out.
 */
export async function waitForProviderToken(
  timeoutMs = 5000,
): Promise<{ providerToken: string | null; providerRefreshToken: string | null }> {
  // Already captured — return immediately
  if (_capturedProviderToken) {
    return consumeProviderToken();
  }

  // Wait for onAuthStateChange to capture it, with a timeout
  await Promise.race([
    new Promise<void>((resolve) => {
      _providerTokenResolve = resolve;
    }),
    new Promise<void>((resolve) => setTimeout(resolve, timeoutMs)),
  ]);

  return consumeProviderToken();
}

/** Convenience alias — most code just needs `supabase.from(...)` */
export const supabase = new Proxy({} as SupabaseClient, {
  get(_target, prop) {
    return Reflect.get(getSupabaseClient(), prop);
  },
});

/**
 * Read the current access token directly from localStorage.
 *
 * This bypasses the Supabase GoTrueClient's internal lock machinery
 * which can deadlock when `getSession()` is called while
 * `onAuthStateChange` holds the internal lock.
 *
 * Falls back to the publishable key if no session is stored
 * (same behaviour as SupabaseClient._getAccessToken).
 */
export function getAccessTokenDirect(): string {
  const projectRef = SUPABASE_URL.replace("https://", "").split(".")[0];
  const storageKey = `sb-${projectRef}-auth-token`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.access_token) return parsed.access_token;
    }
  } catch {
    // Corrupted storage — fall through
  }
  return SUPABASE_PUBLISHABLE_KEY;
}

/** Project URL exported for services that make direct fetch calls. */
export const SUPABASE_PROJECT_URL = SUPABASE_URL;

/** Publishable key exported for apikey header in direct fetch calls. */
export const SUPABASE_KEY = SUPABASE_PUBLISHABLE_KEY;

/**
 * Read the stored session directly from localStorage.
 * Bypasses the GoTrueClient's internal lock that deadlocks during init.
 */
export function getSessionDirect(): { userId: string; email: string } | null {
  const projectRef = SUPABASE_URL.replace("https://", "").split(".")[0];
  const storageKey = `sb-${projectRef}-auth-token`;
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const user = parsed?.user;
    if (!user?.id) return null;
    return { userId: user.id, email: user.email ?? "" };
  } catch {
    return null;
  }
}

const REST_URL = `${SUPABASE_URL}/rest/v1`;

/**
 * Make a direct PostgREST request bypassing the Supabase client.
 * Uses getAccessTokenDirect() so it never deadlocks during bootstrap.
 */
export async function directRestRequest(
  path: string,
  options: { method?: string; body?: string; headers?: Record<string, string> } = {}
): Promise<Response> {
  const token = getAccessTokenDirect();
  return fetch(`${REST_URL}${path}`, {
    method: options.method ?? "GET",
    headers: {
      apikey: SUPABASE_PUBLISHABLE_KEY,
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      ...options.headers,
    },
    body: options.body,
  });
}
